import { useExceptions } from "./exceptions.mjs";
import { useEventBus } from "./events.mjs";
import { topics, useChannels } from "./channels.mjs";
import { clearCart } from "./cart/cart.mjs";
import { useAppRouter } from "./router.mjs";
import { useConfigAppMode } from "./config/app-mode.mjs";
import { clearSpaCookies, setSpaCookie } from "../utils/security.mjs";
import { awaitRefreshToken } from "../utils/async.mjs";
import { isEmail } from "../utils/text.mjs";
import {
  AUTH_COOKIE,
  IS_SECURED_COOKIE,
  REFRESH_AUTH_COOKIE
} from "../const/cookies.mjs";
import { extractQueryName } from "../utils/gql.mjs";
import { keysOf } from "../utils/collections.mjs";
import {
  DeleteUserDocument,
  UpdateCustomerDocument,
  UpdatePasswordDocument
} from "../graphql/Customer.mjs";
import { AUTH_IS_LOGGED_IN, AUTH_USER } from "../utils/provides.mjs";
import {
  GetPhoneNumberDocument,
  IdentityDocument,
  LoginDocument,
  RefreshTokenDocument,
  RegisterDocument,
  RequestResetPasswordByPhoneNumberDocument,
  RequestResetPasswordEmailDocument,
  ResetPasswordDocument,
  RevokeTokenDocument
} from "../graphql/Auth.mjs";
import { makeHttpSimpleJSONRequest } from "../utils/network.mjs";
import { computed, provide, ref } from "vue";
import { useSentryUsers } from "@robustastudio/sentry/user";
import { useMutation, useQuery } from "villus";
import {
  useAsyncData,
  useCookie,
  useRoute,
  useRuntimeConfig,
  useState
} from "#app";
function useStoredUser() {
  const user = useState("user", () => void 0);
  const isPendingAuth = useState("is-pending-auth", () => void 0);
  const { emit } = useEventBus();
  const { setUser: setSentryUser, revokeUser } = useSentryUsers();
  function setUser(data) {
    if (!data) {
      revokeUser();
    }
    user.value = mapUserData(data);
    emit("SET_USER", user.value);
    setSentryUser({
      id: user.value?.id?.toString() ?? "N/A",
      email: user.value?.email ?? "N/A"
    });
  }
  async function getUser() {
    if (isPendingAuth.value) {
      await isPendingAuth.value;
    }
    return user.value ?? null;
  }
  return {
    user,
    isPendingAuth,
    setUser,
    getUser
  };
}
export let REFRESH_TOKEN_HANDLER = null;
export function useAuth() {
  const { appMode } = useConfigAppMode();
  if (appMode === "ssr") {
    return useSsrAuth();
  }
  return useSpaAuth();
}
export function useSsrAuth() {
  const isSecured = useCookie(IS_SECURED_COOKIE);
  const { user, isPendingAuth, setUser } = useStoredUser();
  if (!user.value && isSecured.value) {
    useAsyncData(
      async () => {
        if (isPendingAuth.value && !user.value) {
          await isPendingAuth.value;
          return;
        }
        isPendingAuth.value = useIdentity().identify();
        const fetchedUser = await isPendingAuth.value;
        setUser(fetchedUser);
        isPendingAuth.value = void 0;
      },
      {
        server: false
      }
    );
  }
  const isLoggedIn = computed(() => {
    return !!user.value;
  });
  provide(AUTH_USER, ref(user.value));
  provide(AUTH_IS_LOGGED_IN, isLoggedIn);
  return {
    isLoggedIn,
    user,
    setUser,
    isPendingAuth
  };
}
export function useSpaAuth() {
  const authCookie = useCookie(AUTH_COOKIE);
  const refreshTokenAuthCookie = useCookie(REFRESH_AUTH_COOKIE);
  const { user, setUser, isPendingAuth } = useStoredUser();
  const {
    public: { apiUrl }
  } = useRuntimeConfig();
  if (refreshTokenAuthCookie.value && !authCookie.value) {
    useAsyncData(async () => {
      if (REFRESH_TOKEN_HANDLER === "pending")
        return;
      await resetRefreshToken(
        apiUrl || "/",
        refreshTokenAuthCookie.value?.toString() || ""
      );
    });
  }
  useAsyncData(async () => {
    if (!authCookie.value)
      return;
    if (authCookie.value && user.value?.email)
      return;
    isPendingAuth.value = useIdentity().identify();
    await awaitRefreshToken(REFRESH_TOKEN_HANDLER);
    if (authCookie.value) {
      await useIdentity().identify();
      isPendingAuth.value = void 0;
    }
  });
  const isLoggedIn = computed(() => {
    return !!(user.value && authCookie.value);
  });
  provide(AUTH_USER, user);
  provide(AUTH_IS_LOGGED_IN, isLoggedIn);
  return {
    isLoggedIn,
    user,
    setUser,
    isPendingAuth
  };
}
export function useLogin() {
  const { execute, isFetching } = useMutation(LoginDocument);
  const { execute: getPhoneNumber, isFetching: isGettingPhoneNumber } = useQuery({
    query: GetPhoneNumberDocument,
    fetchOnMount: false
  });
  const { identify } = useIdentity();
  const { emit } = useEventBus();
  const { appMode } = useConfigAppMode();
  async function login(input) {
    try {
      const { data, error } = await execute({
        email: input.phone,
        password: input.password
      });
      if (error) {
        throw new Error(error.message);
      }
      if (!data?.response?.token) {
        throw new Error("No token was generated");
      }
      if (appMode === "spa") {
        setSpaCookie({
          token: data?.response?.token,
          refreshToken: data?.response?.refreshToken,
          expiresAt: Number(data?.response?.token_expires_at),
          refreshTokenExpiresAt: Number(
            data?.response?.refresh_token_expires_at
          )
        });
      }
      const user = await identify();
      if (!user) {
        throw new Error("No user was authenticated with token");
      }
      return user;
    } catch (err) {
      console.log(err?.message);
      if (/\[GraphQL\] This account isn't verified\. Verify by OTP and try again\./.test(
        err?.message
      )) {
        const phoneNumber = isEmail(
          input.phone
          /** It can be phone or email */
        ) ? (await getPhoneNumber({
          variables: {
            email: input.phone
          }
        })).data?.getPhoneNumberByEmail?.phone_number || "" : input.phone;
        await emit("ENTER_OTP", {
          identityString: input.phone,
          readonly: true,
          validationOnly: false,
          mask: phoneNumber
        });
      }
      console.error(err);
      throw err;
    }
  }
  return {
    login,
    isLoggingIn: computed(() => isFetching.value || isGettingPhoneNumber.value)
  };
}
export function useIdentity() {
  const { push } = useChannels();
  const { setUser } = useStoredUser();
  const { execute, isFetching: isFetchingIdentity } = useQuery({
    query: IdentityDocument,
    cachePolicy: "network-only",
    fetchOnMount: false
  });
  async function identify() {
    try {
      const { data, error } = await execute();
      if (error) {
        throw new Error(error.message);
      }
      setUser(data?.customer ?? void 0);
      push(topics.auth.identify, data?.customer);
      return data?.customer;
    } catch (err) {
      if (/The current customer isn't authorized/.test(err?.message)) {
        return;
      }
      console.log(err);
    }
  }
  return {
    identify,
    isFetchingIdentity,
    refetch: execute
  };
}
export function useLogout() {
  const { execute: revokeToken } = useMutation(RevokeTokenDocument);
  const { redirect } = useAppRouter();
  const { push } = useChannels();
  const { user } = useStoredUser();
  const route = useRoute();
  const { emit } = useEventBus();
  function resetUserData() {
    user.value = void 0;
    clearCart();
  }
  function logout() {
    push(topics.auth.logout);
    revokeToken({});
    emit("REVOKE_USER", user.value);
    clearSpaCookies();
    if (/cart/.test(route.fullPath) || /account/.test(route.fullPath) || /order-placed/.test(route.fullPath) || /success/.test(route.fullPath)) {
      redirect("/", {
        // FIXME: using callbacks for now because cannot return promises at the moment
        onComplete: () => {
          resetUserData();
        }
      });
      return;
    }
    resetUserData();
  }
  return {
    logout
  };
}
export function useHeadlessLogout() {
  const { redirect } = useAppRouter();
  const { push } = useChannels();
  const route = useRoute();
  const { user } = useStoredUser();
  function resetUserData() {
    user.value = void 0;
    clearCart();
  }
  function logout() {
    push(topics.auth.logout);
    clearSpaCookies();
    if (/cart/.test(route.fullPath) || /account/.test(route.fullPath) || /order-placed/.test(route.fullPath) || /success/.test(route.fullPath) || /checkout/.test(route.fullPath)) {
      redirect("/login", {
        // FIXME: using callbacks for now because cannot return promises at the moment
        onComplete: () => {
          resetUserData();
        }
      });
      return;
    }
    resetUserData();
  }
  return {
    logout
  };
}
export function useRemoveCustomer() {
  const { logout } = useHeadlessLogout();
  const { execute } = useMutation(DeleteUserDocument);
  const { resolveException } = useExceptions("customer");
  async function deleteUser() {
    try {
      const { error } = await execute();
      if (error && resolveException(error)?.level === "DANGER") {
        throw new Error(resolveException(error)?.message);
      }
      await logout();
    } catch (e) {
      console.log(e);
    }
  }
  return {
    deleteUser
  };
}
export function useCreateCustomer() {
  const { execute, isFetching } = useMutation(RegisterDocument);
  const { login } = useLogin();
  const customerInput = ref({
    fullName: "",
    password: "",
    phone: "",
    email: ""
  });
  async function createCustomer() {
    try {
      const input = fromCustomerInputToVariable(customerInput.value);
      const { data, error } = await execute({ input });
      if (error) {
        throw new Error(error.message);
      }
      if (!data?.response?.customer?.phone_number) {
        throw new Error("No user was created");
      }
      const user = await login({
        phone: data?.response?.customer?.phone_number || "",
        password: customerInput.value.password || ""
      });
      if (!user) {
        throw new Error("No user was authenticated with token");
      }
      return user;
    } catch (err) {
      console.error(err);
      throw err;
    }
  }
  return {
    createCustomer,
    customerInput,
    isCreatingCustomer: isFetching
  };
}
export function useUpdateCustomer() {
  const { execute, isFetching } = useMutation(UpdateCustomerDocument);
  const { user } = useStoredUser();
  async function updateCustomer(input) {
    try {
      const { data, error } = await execute({ input });
      if (error) {
        throw error;
      }
      const updatedUser = mapUserData(data?.response?.customer);
      keysOf(user.value).forEach((key) => {
        const old = user.value?.[key] || "";
        const newVal = updatedUser[key] || old;
        user.value[key] = newVal;
      });
      return data;
    } catch (err) {
      console.error(err);
      throw err;
    }
  }
  return {
    updateCustomer,
    isFetching
  };
}
export function useUpdateCustomerPassword() {
  const { execute, isFetching } = useMutation(UpdatePasswordDocument);
  async function updatePassword(input) {
    try {
      const { data, error } = await execute(input);
      if (error) {
        throw error;
      }
      return data;
    } catch (err) {
      console.error(err);
      throw err;
    }
  }
  return {
    updatePassword,
    isFetching
  };
}
export function useRequestPasswordResetEmail() {
  const { execute, isFetching } = useMutation(
    RequestResetPasswordEmailDocument
  );
  async function requestPasswordResetEmail(email) {
    try {
      const { data, error } = await execute({ email });
      if (error) {
        throw error;
      }
      return data;
    } catch (err) {
      console.error(err);
      throw err;
    }
  }
  return {
    requestPasswordResetEmail,
    isFetching
  };
}
export function useRequestPasswordResetPhoneNumber() {
  const { execute, isFetching: isFetchingResetPassword } = useMutation(
    RequestResetPasswordByPhoneNumberDocument
  );
  async function requestPasswordResetPhoneNumber(phoneNumber) {
    try {
      const { data, error } = await execute({ phoneNumber });
      if (error) {
        throw error;
      }
      return data;
    } catch (err) {
      console.error(err);
      throw err;
    }
  }
  return {
    requestPasswordResetPhoneNumber,
    isFetching: computed(() => isFetchingResetPassword.value)
  };
}
export function useResetPassword() {
  const { execute, isFetching } = useMutation(ResetPasswordDocument);
  async function resetPassword(input) {
    try {
      const { data, error } = await execute(input);
      if (error) {
        throw error;
      }
      return data;
    } catch (err) {
      console.error(err);
      throw err;
    }
  }
  return {
    resetPassword,
    isFetching
  };
}
function mapUserData(user) {
  return {
    // ...user,
    id: user?.id,
    firstname: user?.firstname,
    lastname: user?.lastname,
    email: user?.email,
    phoneNumber: user?.phone_number,
    wishlist: {
      id: user?.wishlist?.id
    },
    totalWishlist: user?.wishlist?.items_count || 0,
    totalOrders: user?.orders?.total_count || 0,
    // user reward points
    ...rewardPointsInfo(user)
  };
}
function rewardPointsInfo(user) {
  return {
    points: user?.reward_points?.balance?.points,
    // exchange rate (  ) the value of money that you can get from the amount of points
    moneyPoints: (user?.reward_points?.exchange_rates?.redemption?.currency_amount || 1) / (user?.reward_points?.exchange_rates?.redemption?.points || 1) * (user?.reward_points?.balance?.points || 0)
  };
}
export function sourceMetaDataPlugin() {
  return function sourceMetaDataPlugin2({ opContext, operation }) {
    opContext.headers.source = `${process.server ? "server" : "browser"}`;
    const queryName = extractQueryName(operation);
    opContext.headers.queryType = operation.type;
    opContext.headers.queryName = queryName;
  };
}
export function spaAuthHeaderPlugin() {
  return function spaAuthHeaderPlugin2({ opContext }) {
    const authCookie = useCookie(AUTH_COOKIE);
    opContext.headers.authorization = `Bearer ${authCookie.value}`;
  };
}
export function refreshAuthPlugin() {
  const { redirect } = useAppRouter();
  const { setUser } = useStoredUser();
  const authCookie = useCookie(AUTH_COOKIE);
  const refreshTokenAuthCookie = useCookie(REFRESH_AUTH_COOKIE);
  const {
    public: { apiUrl }
  } = useRuntimeConfig();
  return async function refreshAuthPlugin2({ afterQuery }) {
    afterQuery((result) => {
      if (result.error && (/The current user isn\\'t authorized/.test(result.error.message) || /The current user isn't authorized/.test(result.error.message))) {
        setUser(null);
        clearSpaCookies();
        redirect("/login");
      }
    });
    if (process.server)
      return;
    if (authCookie.value)
      return;
    if (!refreshTokenAuthCookie.value)
      return;
    const count = await awaitRefreshToken(REFRESH_TOKEN_HANDLER);
    if (count > 0) {
      return;
    }
    await resetRefreshToken(
      apiUrl || "/",
      refreshTokenAuthCookie.value.toString()
    );
  };
}
export async function resetRefreshToken(apiUrl, refreshToken) {
  if (REFRESH_TOKEN_HANDLER === "pending")
    return;
  REFRESH_TOKEN_HANDLER = "pending";
  const { data } = await makeHttpSimpleJSONRequest(
    "post",
    apiUrl ?? "",
    {
      query: normalizeQuery(RefreshTokenDocument),
      variables: {
        refreshToken
      }
    },
    {}
  );
  if (data?.response?.token) {
    if (process.client) {
      setSpaCookie({
        token: data?.response?.token,
        refreshToken: data?.response?.refreshToken,
        expiresAt: data?.response?.token_expires_at,
        refreshTokenExpiresAt: data?.response?.refresh_token_expires_at
      });
    }
    if (process.server) {
      console.warn("[WARN] Using Spa app setting within SSR");
    }
  }
  REFRESH_TOKEN_HANDLER = null;
}
export function useCleanAuth() {
  const { user } = useStoredUser();
  user.value = void 0;
}
function fromCustomerInputToVariable(input) {
  return {
    email: input.email,
    firstname: input.fullName.trim().split(" ")[0],
    lastname: input.fullName.trim().split(" ").slice(1).join(" "),
    phone_number: input.phone,
    password: input.password
  };
}
